import { prepareEnvironment } from '@gmrchk/cli-testing-library';
import { afterAll, beforeAll, vi } from 'vitest';
import { undefiny } from './helpers';
import type { Defs, MockFn, MockFsKeys } from './types';

export const envFn = <K extends keyof Defs>() => {
  type T = Defs[K];
  return vi.fn<T[0], T[1]>();
};

export const useBefore = () => {
  const readFile = envFn<'readFile'>();
  const writeFile = envFn<'writeFile'>();
  const cleanup = envFn<'cleanup'>();
  const ls = envFn<'ls'>();
  const removeDir = envFn<'removeDir'>();
  const execute = envFn<'execute'>();
  const spawn = envFn<'spawn'>();
  const removeFile = envFn<'removeFile'>();
  const exists = envFn<'exists'>();
  const makeDir = envFn<'makeDir'>();
  const path = vi.fn();

  beforeAll(async () => {
    const cli = await prepareEnvironment();

    // #region Implements functions
    readFile.mockImplementation(cli.readFile);
    writeFile.mockImplementation(cli.writeFile);
    cleanup.mockImplementation(cli.cleanup);
    ls.mockImplementation(cli.ls);
    removeDir.mockImplementation(cli.removeDir);
    execute.mockImplementation(cli.execute);
    spawn.mockImplementation(cli.spawn);
    makeDir.mockImplementation(cli.makeDir);
    path.mockReturnValue(cli.path);
    removeFile.mockImplementation(cli.removeFile);
    exists.mockImplementation(cli.exists);
    // #endregion
  });

  return {
    readFile,
    ls,
    removeDir,
    execute,
    writeFile,
    spawn,
    cleanup,
    makeDir,
    path,
    removeFile,
    exists,
    read: undefiny<string>(),
  };
};

export const buildFn = (_fn?: MockFn) => {
  const fn = () => {
    const {
      readFile,
      ls,
      removeDir,
      execute,
      writeFile,
      spawn,
      cleanup,
      makeDir,
      path,
      removeFile,
      exists,
    } = useBefore();

    const mockFs = (...envs: MockFsKeys[]) => {
      const __fn = globalThis?.buildMockFs?.getMockImplementation();
      /* v8 ignore next 1 */
      if (!__fn) return false;

      globalThis.buildMockFs(
        { exists, makeDir, removeDir, ls, readFile, writeFile },
        ...envs,
      );

      return true;
    };

    afterAll(async () => {
      await cleanup();
      // vi.clearAllMocks();
    });

    return _fn?.({
      readFile,
      ls,
      removeDir,
      execute,
      writeFile,
      spawn,
      cleanup,
      makeDir,
      path: path(),
      removeFile,
      mockFs,
      exists,
    });
  };

  return fn;
};
